/**
 * Şablon sekmesi — "bir referans belge göster, gerisini aynı şekilde oku".
 *
 * AKIŞ
 *   1. Referans belge yüklenir; konumlu metin parçaları çıkarılır.
 *   2. Sistem aday alanları ÖNERİR (etiket + sağındaki/altındaki değer).
 *   3. Kullanıcı hangilerini istediğini seçer ve şablonu adlandırır.
 *   4. Sonraki belgeler o şablonla otomatik dolar.
 *
 * Neden serbest tıklama değil de öneri listesi: kullanıcıdan bir PDF'in
 * üzerinde tek tek kutu işaretlemesini istemek hem yavaş hem hataya açık.
 * Aday çiftleri çıkarıp "bunlardan hangileri?" diye sormak aynı sonucu
 * birkaç tıkla veriyor.
 *
 * Şablonlar TARAYICIDA saklanır (localStorage). Sunucuya gönderilmiyorlar
 * çünkü bir şablon müşterinin fatura düzenini tarif eder — bu bilgi
 * cihazdan çıkmak zorunda değil. İleride hesaba bağlamak istenirse
 * saklama katmanı değişir, motor değişmez.
 */

import { legacy } from '../../shell/bridge.ts';
import {
  learnAnchor, applyTemplate, templateFit,
  type TextItem, type Template, type FieldAnchor, type FieldKind,
} from '@tekoffice/extract';

const STORE_KEY = 'tek_templates_v1';

interface PdfLike {
  numPages: number;
  getPage(n: number): Promise<{ getTextContent(o?: unknown): Promise<{ items?: unknown[] }> }>;
}

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function t(k: string): string { return legacy.t(k); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }

/* ── Saklama ───────────────────────────────────────────────────────── */

export function loadTemplates(): Template[] {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function saveTemplates(list: readonly Template[]): void {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(list)); }
  catch { legacy.toast(t('aip_tpl_save_fail')); }
}

/* ── PDF'ten konumlu metin ─────────────────────────────────────────── */

/**
 * pdf.js'in metin parçalarını konumlarıyla al.
 *
 * `pdfLinesFromText` satır birleştiriyor ve konumu kaybediyor; şablon
 * motorunun tam olarak o konuma ihtiyacı var.
 */
export async function itemsFromPdf(buf: ArrayBuffer, maxPages = 5): Promise<TextItem[]> {
  const lib = (globalThis as unknown as { pdfjsLib?: { getDocument(o: unknown): { promise: Promise<PdfLike> } } }).pdfjsLib;
  if (!lib) return [];
  const pdf = await lib.getDocument({ data: new Uint8Array(buf), useSystemFonts: true }).promise;
  const out: TextItem[] = [];
  const n = Math.min(pdf.numPages, maxPages);
  for (let p = 1; p <= n; p++) {
    const page = await pdf.getPage(p);
    const tc = await page.getTextContent({ normalizeWhitespace: true });
    for (const raw of (tc.items ?? [])) {
      const it = raw as { str?: string; transform?: number[] };
      if (!it.str || !it.str.trim() || !it.transform) continue;
      /* Sayfaları üst üste bindirmemek için her sayfaya büyük bir y kayması
         verilir; aksi hâlde 2. sayfanın başlığı 1. sayfanın altıyla aynı
         hizaya düşer ve çıpalar birbirine karışır. */
      out.push({ str: it.str, x: it.transform[4], y: it.transform[5] + (n - p) * 100000 });
    }
  }
  return out;
}

/* ── Aday alan önerisi ─────────────────────────────────────────────── */

export interface Candidate {
  readonly index: number;
  readonly label: string;
  readonly value: string;
  readonly kind: FieldKind;
  readonly anchor: FieldAnchor;
}

function guessKind(s: string): FieldKind {
  const v = s.trim();
  if (/^\d{1,4}[.\-/]\d{1,2}[.\-/]\d{2,4}$/.test(v)) return 'date';
  if (/\d/.test(v) && /^[-+(]?[\d.,\s']+\)?$/.test(v)) return 'amount';
  return 'text';
}

/**
 * Belgedeki "etiket → değer" adaylarını çıkar.
 *
 * Yalnızca ÇIPASI BULUNAN değerler aday olur; çıpasız bir değer sonraki
 * belgede güvenilir biçimde bulunamaz ve öneri listesine girmesi kullanıcıyı
 * yanıltır.
 */
export function proposeFields(items: readonly TextItem[]): Candidate[] {
  const out: Candidate[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < items.length; i++) {
    const v = items[i].str.trim();
    if (!v || v.length > 60) continue;
    const kind = guessKind(v);
    if (kind === 'text' && !/\d/.test(v)) continue;    // düz başlıklar değer değil
    const anchor = learnAnchor(items, i, 'field' + i, kind);
    if (!anchor) continue;
    const key = anchor.label + '|' + anchor.direction;
    if (seen.has(key)) continue;                        // etiket başına bir aday
    seen.add(key);
    out.push({ index: i, label: anchor.label, value: v, kind, anchor });
  }
  return out.slice(0, 40);
}

/* ── Arayüz ────────────────────────────────────────────────────────── */

let candidates: Candidate[] = [];
let picked = new Set<number>();

export function renderTemplateTab(host: HTMLElement): void {
  const list = loadTemplates();
  host.innerHTML =
    '<div class="sc">'
    + '<div class="pt">' + esc(t('aip_tpl_t')) + '</div>'
    + '<div class="ps">' + esc(t('aip_tpl_d')) + '</div>'
    + '<input type="file" id="aip-ref-inp" style="display:none" accept=".pdf" onchange="aipLearnRef(event)">'
    + '<div class="card" style="margin-bottom:12px">'
    + '<button class="btn br bfu" onclick="document.getElementById(\'aip-ref-inp\').click()">'
    + esc(t('aip_tpl_upload')) + '</button>'
    + '<div id="aip-tpl-out" style="margin-top:12px"></div>'
    + '</div>'
    + '<div class="card"><div class="set">' + esc(t('aip_tpl_saved')) + '</div>'
    + '<div id="aip-tpl-list">' + renderList(list) + '</div></div>'
    + '</div>';
}

function renderList(list: readonly Template[]): string {
  if (!list.length) {
    return '<div style="font-size:12px;color:var(--t2)">' + esc(t('aip_tpl_none')) + '</div>';
  }
  return list.map((tpl) =>
    '<div style="display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--div)">'
    + '<span style="flex:1;font-size:13px;font-weight:600">' + esc(tpl.name) + '</span>'
    + '<span style="font-size:11px;color:var(--t2)">' + tpl.fields.length + ' ' + esc(t('aip_tpl_fields')) + '</span>'
    + '<button class="bsm" style="background:var(--rA);color:var(--red);border:1px solid var(--rB)"'
    + ' onclick="aipDeleteTemplate(\'' + esc(tpl.id) + '\')">' + esc(t('aip_tpl_del')) + '</button>'
    + '</div>').join('');
}

/** Referans belge yüklendi: adayları çıkar ve seçtir. */
export async function learnFromReference(file: File): Promise<void> {
  const out = el('aip-tpl-out');
  if (!out) return;
  out.innerHTML = '<div style="font-size:12px;color:var(--t2)">⏳</div>';

  let items: TextItem[] = [];
  try { items = await itemsFromPdf(await file.arrayBuffer()); }
  catch { items = []; }

  if (!items.length) {
    out.innerHTML = '<div style="color:var(--red);font-size:12px">'
      + esc(t('aip_tpl_notext')) + '</div>';
    return;
  }

  candidates = proposeFields(items);
  picked = new Set(candidates.map((_, i) => i));   // hepsi açık başlar

  if (!candidates.length) {
    out.innerHTML = '<div style="color:var(--red);font-size:12px">'
      + esc(t('aip_tpl_nofields')) + '</div>';
    return;
  }

  out.innerHTML =
    '<div style="font-size:12px;color:var(--t2);margin-bottom:8px">'
    + esc(t('aip_tpl_pick')) + '</div>'
    + candidates.map((c, i) =>
      '<label style="display:flex;align-items:center;gap:8px;padding:7px 0;border-bottom:1px solid var(--div);cursor:pointer">'
      + '<input type="checkbox" checked onchange="aipTogglePick(' + i + ',this.checked)" style="accent-color:var(--red)">'
      + '<span style="flex:1;font-size:12px"><b>' + esc(c.label) + '</b>'
      + ' <span style="color:var(--t2)">→</span> ' + esc(c.value) + '</span>'
      + '<span style="font-size:10px;color:var(--t2)">' + esc(c.kind) + '</span>'
      + '</label>').join('')
    + '<div style="display:flex;gap:8px;margin-top:12px">'
    + '<input class="inp" id="aip-tpl-name" placeholder="' + esc(t('aip_tpl_name')) + '" value="'
    + esc(file.name.replace(/\.[^.]+$/, '')) + '">'
    + '<button class="btn br bsm" style="flex-shrink:0" onclick="aipSaveTemplate()">'
    + esc(t('aip_tpl_save')) + '</button></div>';
}

export function togglePick(i: number, on: boolean): void {
  if (on) picked.add(i); else picked.delete(i);
}

export function saveCurrent(): void {
  const nameInput = el('aip-tpl-name') as HTMLInputElement | null;
  const name = (nameInput?.value || '').trim();
  if (!name) { legacy.toast(t('aip_tpl_needname')); return; }

  const fields: FieldAnchor[] = candidates
    .filter((_, i) => picked.has(i))
    .map((c) => ({ ...c.anchor, field: c.label.replace(/[:\s]+$/, '') }));

  if (!fields.length) { legacy.toast(t('aip_tpl_needfield')); return; }

  const list = loadTemplates();
  list.push({ id: 'tpl_' + Date.now().toString(36), name, fields });
  saveTemplates(list);

  const out = el('aip-tpl-out');
  if (out) out.innerHTML = '<div style="color:var(--grn);font-size:12px;font-weight:700">✓ '
    + esc(t('aip_tpl_saved_ok')) + '</div>';
  const listHost = el('aip-tpl-list');
  if (listHost) listHost.innerHTML = renderList(loadTemplates());
}

export function deleteTemplate(id: string): void {
  saveTemplates(loadTemplates().filter((x) => x.id !== id));
  const listHost = el('aip-tpl-list');
  if (listHost) listHost.innerHTML = renderList(loadTemplates());
}

/**
 * Kayıtlı şablonlardan bu belgeye EN İYİ uyanı seç.
 *
 * Eşik bilinçli olarak yüksek (0.6): yarısı tutmayan bir şablonu uygulamak,
 * yanlış hücreleri doğru sanmak demektir. Uymuyorsa null döner ve belge
 * normal çıkarım yoluna gider.
 */
export function bestTemplate(items: readonly TextItem[]): { tpl: Template; fit: number } | null {
  let best: { tpl: Template; fit: number } | null = null;
  for (const tpl of loadTemplates()) {
    const fit = templateFit(items, tpl);
    if (!best || fit > best.fit) best = { tpl, fit };
  }
  return best && best.fit >= 0.6 ? best : null;
}

export { applyTemplate };
